/**
 * DashboardLayout Component
 * Main layout with static sidebar, header bar, and content area
 */

import * as React from 'react';
import { Sidebar } from '@/components/layout/Sidebar';
import { MobileBottomNav } from '@/components/layout/MobileBottomNav';
import { GlobalAnalysisDialog } from '@/components/analysis/GlobalAnalysisDialog/GlobalAnalysisDialog';
import { AnalystContactDialog } from '@/components/common/AnalystContactDialog/AnalystContactDialog';
import { ProjectPlanDialog } from '@/components/common/ProjectPlanDialog/ProjectPlanDialog';
import { FloatingOptInButton } from '@/components/common/FloatingOptInButton';
import { cn } from '@/lib/utils';

export interface DashboardLayoutProps {
  /** Header component */
  header?: React.ReactNode;
  /** Footer component */
  footer?: React.ReactNode;
  /** Main page content */
  children: React.ReactNode;
  /** Additional class names for the main content area */
  className?: string;
}

/**
 * Dashboard layout with static sidebar, header, and content area
 */
export function DashboardLayout({
  header,
  footer,
  children,
  className,
}: DashboardLayoutProps) {
  return (
    <div className="min-h-screen bg-[var(--color-dashboard-bg)] flex">
      {/* Static Sidebar — hidden on mobile, flex on md+ */}
      <Sidebar className="hidden md:flex shrink-0 sticky top-0 h-screen" />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Header */}
        {header}

        {/* Main Content */}
        <main
          className={cn(
            'flex-1 min-w-0 w-full',
            'px-4 py-4 pb-20',
            'md:px-8 md:py-6 md:pb-0',
            'lg:px-12 lg:py-8',
            className
          )}
        >
          {children}
        </main>

        {/* Footer */}
        {footer}
      </div>

      {/* Mobile bottom navigation */}
      <MobileBottomNav />

      {/* Global analysis dialog — can be opened from anywhere via store */}
      <GlobalAnalysisDialog />

      {/* Analyst contact dialog — shown when clicking eclou.analyst */}
      <AnalystContactDialog />

      {/* Project plan dialog — shown when clicking "Projektplan anfordern" */}
      <ProjectPlanDialog />

      {/* Floating opt-in button */}
      <FloatingOptInButton />
    </div>
  );
}
